import React, { useState, useEffect } from 'react';
import { socialAPI, aiExtrasAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Share2, Plus, Trash2, Send, Clock, BarChart3, Twitter, Linkedin, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_ICONS = { twitter: Twitter, linkedin: Linkedin, facebook: Facebook, instagram: Instagram };
const PLATFORM_COLORS = { twitter: '#1DA1F2', linkedin: '#0A66C2', facebook: '#1877F2', instagram: '#E1306C' };

export default function AdminSocialMedia() {
  const [accounts, setAccounts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [accountForm, setAccountForm] = useState({ platform: 'twitter', handle: '', access_token: '' });
  const [schedForm, setSchedForm] = useState({ platform: 'twitter', content: '', scheduled_at: '', image_url: '' });
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ title: '', content: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [accRes, schedRes, anaRes] = await Promise.all([
        socialAPI.listAccounts().catch(() => ({ data: { accounts: [] } })),
        socialAPI.listScheduled().catch(() => ({ data: { posts: [] } })),
        socialAPI.analytics(30).catch(() => ({ data: null })),
      ]);
      setAccounts(accRes.data.accounts || []);
      setScheduled(schedRes.data.posts || []);
      setAnalytics(anaRes.data);
    } catch { toast.error('Failed to load social data'); }
    finally { setLoading(false); }
  };

  const addAccount = async () => {
    try {
      await socialAPI.addAccount(accountForm);
      toast.success('Account added');
      setShowAddAccount(false);
      setAccountForm({ platform: 'twitter', handle: '', access_token: '' });
      loadAll();
    } catch { toast.error('Failed to add account'); }
  };

  const removeAccount = async (id) => {
    try { await socialAPI.removeAccount(id); toast.success('Account removed'); loadAll(); }
    catch { toast.error('Failed'); }
  };

  const schedulePost = async () => {
    try {
      await socialAPI.schedulePost(schedForm);
      toast.success('Post scheduled');
      setShowSchedule(false);
      setSchedForm({ platform: 'twitter', content: '', scheduled_at: '', image_url: '' });
      loadAll();
    } catch { toast.error('Failed to schedule'); }
  };

  const postNow = async (post) => {
    try {
      await socialAPI.postNow({ platform: post.platform, content: post.content });
      toast.success(`Posted to ${post.platform}`);
    } catch { toast.error('Failed to post'); }
  };

  const generateAIContent = async () => {
    if (!aiPrompt.title) { toast.error('Enter a title'); return; }
    setGeneratingAI(true);
    try {
      const r = await aiExtrasAPI.socialPost({ title: aiPrompt.title, content: aiPrompt.content, platforms: [schedForm.platform] });
      const posts = r.data.posts || {};
      const generated = posts[schedForm.platform] || Object.values(posts)[0] || '';
      setSchedForm(f => ({ ...f, content: generated }));
      toast.success('AI content generated');
    } catch { toast.error('AI generation failed'); }
    finally { setGeneratingAI(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Share2 className="w-6 h-6 text-[#B89B5E]" /> Social Media Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">Schedule posts, manage accounts, track engagement</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAddAccount(true)}><Plus className="w-4 h-4 mr-2" /> Add Account</Button>
          <Button onClick={() => setShowSchedule(true)} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Clock className="w-4 h-4 mr-2" /> Schedule Post</Button>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              <p className="text-2xl font-bold">{analytics.total_posts}</p>
              <p className="text-xs text-[#5B616E] mt-1">Total posts (30d)</p>
            </CardContent>
          </Card>
          {(analytics.by_platform || []).map(p => {
            const Icon = PLATFORM_ICONS[p.platform] || Share2;
            return (
              <Card key={p.platform} className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" style={{ color: PLATFORM_COLORS[p.platform] }} />
                    <span className="text-xs capitalize text-[#5B616E]">{p.platform}</span>
                  </div>
                  <p className="text-2xl font-bold">{p.count}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connected accounts */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Connected Accounts</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-20" /> : accounts.length === 0 ? (
              <p className="text-sm text-[#5B616E] text-center py-4">No accounts connected. Add your first social account.</p>
            ) : accounts.map(a => {
              const Icon = PLATFORM_ICONS[a.platform] || Share2;
              return (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-[#E7E0D6] last:border-0">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: PLATFORM_COLORS[a.platform] }} />
                    <span className="text-sm font-medium">{a.handle}</span>
                    <Badge variant="outline" className="text-[10px]">{a.platform}</Badge>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => removeAccount(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Scheduled posts */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Scheduled Posts</CardTitle></CardHeader>
          <CardContent>
            {loading ? <Skeleton className="h-20" /> : scheduled.length === 0 ? (
              <p className="text-sm text-[#5B616E] text-center py-4">No scheduled posts. Use "Schedule Post" to add one.</p>
            ) : scheduled.map(p => {
              const Icon = PLATFORM_ICONS[p.platform] || Share2;
              return (
                <div key={p.id} className="p-3 border border-[#E7E0D6] rounded-lg mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5" style={{ color: PLATFORM_COLORS[p.platform] }} />
                      <span className="text-xs capitalize text-[#5B616E]">{p.platform}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => postNow(p)}><Send className="w-3 h-3 mr-1" />Post now</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-red-500 p-1" onClick={async () => { await socialAPI.deletePost(p.id); loadAll(); }}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <p className="text-xs">{p.content}</p>
                  {p.scheduled_at && <p className="text-[10px] text-[#5B616E] mt-1"><Clock className="w-3 h-3 inline mr-1" />{new Date(p.scheduled_at).toLocaleString()}</p>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Add Account */}
      <Dialog open={showAddAccount} onOpenChange={setShowAddAccount}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Social Account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={accountForm.platform} onValueChange={v => setAccountForm(f => ({ ...f, platform: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['twitter', 'facebook', 'linkedin', 'instagram'].map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="@handle or page name" value={accountForm.handle} onChange={e => setAccountForm(f => ({ ...f, handle: e.target.value }))} />
            <Input placeholder="Access token (keep secure)" type="password" value={accountForm.access_token} onChange={e => setAccountForm(f => ({ ...f, access_token: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddAccount(false)}>Cancel</Button>
            <Button onClick={addAccount} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Add Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Post */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Schedule Post</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={schedForm.platform} onValueChange={v => setSchedForm(f => ({ ...f, platform: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['twitter', 'facebook', 'linkedin', 'instagram'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <div className="border border-dashed border-[#E7E0D6] rounded-xl p-3 space-y-2">
              <p className="text-xs font-medium text-[#5B616E]">AI Content Generator</p>
              <Input placeholder="Article headline for AI..." value={aiPrompt.title} onChange={e => setAiPrompt(f => ({ ...f, title: e.target.value }))} />
              <Button variant="outline" size="sm" onClick={generateAIContent} disabled={generatingAI} className="w-full text-xs">
                {generatingAI ? 'Generating…' : '✨ Generate with AI'}
              </Button>
            </div>
            <Textarea rows={4} placeholder="Post content..." value={schedForm.content} onChange={e => setSchedForm(f => ({ ...f, content: e.target.value }))} />
            <Input placeholder="Image URL (optional)" value={schedForm.image_url} onChange={e => setSchedForm(f => ({ ...f, image_url: e.target.value }))} />
            <Input type="datetime-local" value={schedForm.scheduled_at} onChange={e => setSchedForm(f => ({ ...f, scheduled_at: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSchedule(false)}>Cancel</Button>
            <Button onClick={schedulePost} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
